import { toast } from "react-toastify";
import i18n from "../i18n/locales/config";

/**
 * Every message the app shows in a toast, in the visitor's language.
 *
 * The server answers in English — 134 fixed sentences, and the interface used
 * to put 101 of them on screen exactly as they arrived, on a Portuguese site.
 * Translating them on the server would still leave the interface reading its
 * words off an API, one missed sentence at a time. So the words live here:
 * every action names the sentence it wants said, the server's answer is only
 * consulted when it is one of the situations the interface knows about, and
 * its raw text appears only when nobody planned for what happened (a 500, or
 * no answer at all) — where a detail is better than a shrug.
 */
const OPTS = {
  autoClose: 2500,
  className: "toast-message",
  position: toast.POSITION.TOP_RIGHT,
};

/**
 * The server's sentences the interface has its own words for. Keyed by the
 * text exactly as the API sends it, whether in `message`, in `data.error`, or
 * as the first line of a field error. Anything not here falls back to what
 * the action asked for.
 */
const KNOWN = {
  "Authentication failed. Please check your credentials.": "Errors.Credentials",
  "Unauthorised.": "Errors.Credentials",
  "You are not an active user. Please contact EasyProperty support.": "Errors.Inactive",
  "You are not an active agent. Please contact the EasyProperty support team.": "Errors.Inactive",
  "Please wait for Admin approval.": "Errors.AwaitingApproval",
  "We could not find your email.": "Errors.EmailUnknown",
  "The email has already been taken.": "Errors.EmailTaken",
  "The contact number has already been taken.": "Errors.PhoneTaken",
  "Please verify your e-mail address before finishing.": "Errors.VerifyEmailFirst",
  "Use the e-mail address the invitation was sent to.": "Errors.InviteEmailMismatch",
  "This invitation has already been used.": "Errors.InviteUsed",
  "Invitation not found.": "Errors.InviteNotFound",
  "Already claimed.": "Errors.AuditTaken",
  "This property was just taken by another agent.": "Errors.AuditTaken",
  "Expired.": "Errors.ReceiptExpired",
  "This sign-in has already been completed or has expired. Please try again.": "Errors.ReceiptExpired",
  "The provided password does not match your current password.": "Errors.CurrentPassword",
  "The new password field and current password must be different.": "Errors.SamePassword",
  "Schedule date has not passed.": "Errors.DateNotPassed",
  "Schedule date is not passed": "Errors.DateNotPassed",
  "Too many codes requested. Please try again later.": "Errors.TooMany",
  "Property not found.": "Errors.NotFound",
  "Rating not found.": "Errors.NotFound",
  "User not found.": "Errors.NotFound",
  "Category not found.": "Errors.NotFound",
  "Visit not found or does not belong to you.": "Errors.NotFound",
  "Error! Wishlist item not found.": "Errors.NotFound",
  "Invalid token!": "Errors.ResetLinkInvalid",
  "This e-mail address already has an account. Sign in with it instead.": "Errors.SocialEmailTaken",
};

/** What to say for a status code when the action gave no sentence of its own. */
const BY_STATUS = {
  401: "Errors.Credentials",
  403: "Errors.Forbidden",
  404: "Errors.NotFound",
  409: "Errors.Conflict",
  410: "Errors.ReceiptExpired",
  422: "Errors.Invalid",
  429: "Errors.TooMany",
};

/** The interceptor hands the failed response itself to the caller. */
const bodyOf = (e) => e?.data ?? e?.response?.data ?? {};
const statusOf = (e) => e?.status ?? e?.response?.status;

/** Every sentence the server put in the answer, wherever it put it. */
const sentencesOf = (e) => {
  const body = bodyOf(e);
  const out = [];
  const push = (v) => {
    if (typeof v === "string" && v.trim()) out.push(v.trim());
  };
  push(body.message);
  push(body.message?.error);
  push(body.data?.error);
  if (body.data && typeof body.data === "object") {
    Object.values(body.data).forEach((v) => {
      if (Array.isArray(v)) push(v[0]);
    });
  }
  return out;
};

/** The server's own words, for the cases nobody planned a sentence for. */
export const serverMessage = (e) => sentencesOf(e)[0] || e?.message || "";

/** Something went right. `key` names the sentence; `values` fill its blanks. */
export const say = (key, values) => toast.success(i18n.t(key, values), OPTS);

/**
 * Something went wrong.
 *
 * `e` is what the request threw (may be null for a purely local complaint).
 * `key` is the sentence for this action when the answer says nothing the
 * interface recognises. Order of preference: a situation the server named and
 * we have words for; then the action's own sentence; then the status code;
 * then the generic one. The server's raw text is appended only when the
 * failure was unexpected — a 500 or no answer — so a support ticket has
 * something to go on.
 */
export const complain = (e, key, values) => {
  const status = statusOf(e);

  /* Said once, elsewhere, for the whole burst: see AxiosInterceptor. */
  if (status === 429) return;

  const known = sentencesOf(e).map((s) => KNOWN[s]).find(Boolean);
  const main = i18n.t(known || key || BY_STATUS[status] || "Errors.Generic", values);

  const unexpected = e && (!status || status >= 500);
  const detail = unexpected ? serverMessage(e) : "";

  toast.error(detail && detail !== main ? `${main} (${detail})` : main, {
    ...OPTS,
    autoClose: 3500,
  });
};

/** A heads-up that is neither. */
export const warn = (key, values) =>
  toast.warning(i18n.t(key, values), { ...OPTS, autoClose: 4000 });
